import { EXTRACT_OCR_STATUS, SOURCE_DOCUMENT_SOURCES } from "core/consts";
import { useExtractOCR } from "context/useExtractOCR";
import { useExtractOCRStatus } from "context/useExtractOCRStatus";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import {
  Auction,
  ClinicalConsultation,
  DocsFormSourceDocument,
  ExtractOCRStatus,
  ExtractOCRStatusResult,
  FileState,
  Patient,
} from "core/types";
import { isFileV2 } from "core/model/files/utils";
import { ReactNode, createContext, useContext, useMemo } from "react";

export type ExtractDocsContextValue = {
  extractOcr: ReturnType<typeof useExtractOCR>;
  extractOcrStatus: ReturnType<typeof useExtractOCRStatus>;
  utils: {
    getExtractedDocument: (
      file: FileState,
    ) => ExtractOCRStatusResult | undefined;
    isFileExtracted: (file: FileState) => boolean;
    isFileExtractionFailed: (file: FileState) => boolean;
    isFileExtractionProcessing: (file: FileState) => boolean;
  };
};

type ExtractDocsProviderProps = {
  auction?: Auction;
  consultation?: ClinicalConsultation;
  children: ReactNode;
  patient: Patient;
};

type SourceDocumentFileMatcher = Pick<
  DocsFormSourceDocument,
  "source" | "recare_file_id" | "kis_file_id"
> & {
  document_id: DocsFormSourceDocument["id"];
};

const PROCESSING_STATUS: ExtractOCRStatus[] = [
  EXTRACT_OCR_STATUS.PROCESSING,
  EXTRACT_OCR_STATUS.PENDING,
];

export const ExtractDocsContext = createContext<
  ExtractDocsContextValue | undefined
>(undefined);

function doesSourceDocumentMatchFile(
  doc: SourceDocumentFileMatcher,
  file: FileState,
): boolean {
  switch (doc.source) {
    case SOURCE_DOCUMENT_SOURCES.RECARE_FILE:
      return String(doc.recare_file_id) === String(file.id);

    case SOURCE_DOCUMENT_SOURCES.KIS_FILE: {
      const matchesUploadedHisId =
        isFileV2(file) &&
        file.kis_document_id_string &&
        doc.kis_file_id === file.kis_document_id_string;

      return (
        matchesUploadedHisId || String(doc.kis_file_id) === String(file.id)
      );
    }
    case SOURCE_DOCUMENT_SOURCES.KIS_DATA:
    case SOURCE_DOCUMENT_SOURCES.RECARE_DATA:
      return String(doc.document_id) === String(file.id);

    default:
      return false;
  }
}

export function getExtractedDocumentFromFile(
  documents: ExtractOCRStatusResult[] | undefined,
  file: FileState,
): ExtractOCRStatusResult | undefined {
  return documents?.find((doc) => doesSourceDocumentMatchFile(doc, file));
}

export function getFileForSourceDocument(
  files: { name: string; file: FileState }[],
  sourceDocument: Pick<
    DocsFormSourceDocument,
    "id" | "recare_file_id" | "kis_file_id" | "source"
  >,
): { name: string; file: FileState } | undefined {
  return files.find(({ file }) =>
    doesSourceDocumentMatchFile(
      {
        document_id: sourceDocument.id,
        kis_file_id: sourceDocument.kis_file_id,
        recare_file_id: sourceDocument.recare_file_id,
        source: sourceDocument.source,
      },
      file,
    ),
  );
}

export const ExtractDocsProvider = ({
  auction,
  children,
  consultation,
  patient,
}: ExtractDocsProviderProps) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const extractOcrStatus = useExtractOCRStatus({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    patientId: patient.id,
    skip: !isDocsAndExtractActive && !isDischargeLetterActive,
  });
  const extractOcr = useExtractOCR({
    auctionId: auction?.id,
    careseekerId: patient.careseeker?.id,
    consultationId: consultation?.id,
    externalId: patient.external_id,
    ocrStatus: extractOcrStatus.result,
    ocrStatusError: extractOcrStatus.error,
    onComplete: extractOcrStatus.refetch,
    patientId: patient.id,
  });

  const getExtractedDocument = (
    file: FileState,
  ): ExtractOCRStatusResult | undefined => {
    return getExtractedDocumentFromFile(
      extractOcrStatus.result?.documents,
      file,
    );
  };

  const isFileExtracted = (file: FileState): boolean => {
    return getExtractedDocument(file)?.status === EXTRACT_OCR_STATUS.EXTRACTED;
  };

  const isFileExtractionProcessing = (file: FileState): boolean => {
    const document = getExtractedDocument(file);
    if (!document) {
      return extractOcr.processingFileIds.includes(file.id);
    }

    return PROCESSING_STATUS.includes(document.status);
  };

  const isFileExtractionFailed = (file: FileState): boolean => {
    return getExtractedDocument(file)?.status === EXTRACT_OCR_STATUS.FAILED;
  };

  const utils = {
    getExtractedDocument,
    isFileExtracted,
    isFileExtractionFailed,
    isFileExtractionProcessing,
  };

  const value: ExtractDocsContextValue = useMemo(
    () => ({
      extractOcr,
      extractOcrStatus,
      utils,
    }),
    [extractOcr, extractOcrStatus, utils],
  );

  return (
    <ExtractDocsContext.Provider value={value}>
      {children}
    </ExtractDocsContext.Provider>
  );
};

export const useExtractDocs = (): ExtractDocsContextValue => {
  const context = useContext(ExtractDocsContext);

  if (context === undefined) {
    throw new Error(
      "useExtractDocs must be used within an ExtractDocsProvider",
    );
  }

  return context;
};
